/**
 * Shared helpers for Swiss VRG (recycling fee) cart lines.
 * Carrier ↔ fee pairing uses the hidden line property `_vrg_for`.
 */
window.DobiVrgCart = window.DobiVrgCart || (() => {
  const VRG_FOR_KEY = '_vrg_for';
  let syncing = false;

  function getRoutes() {
    return window.theme?.routes || {};
  }

  function parseVariantId(value) {
    if (value == null || value === '') return null;
    const match = String(value).match(/(\d+)\s*$/);
    if (!match) return null;
    const id = Number(match[1]);
    return Number.isFinite(id) && id > 0 ? id : null;
  }

  function getVrgVariantIdFromForm(form) {
    if (!form) return null;

    const selectedId = form.querySelector('[name="id"]')?.value;
    const mapEl = form.querySelector('[data-vrg-variant-map]');
    if (mapEl) {
      try {
        const map = JSON.parse(mapEl.textContent || '{}');
        const fromMap = parseVariantId(map[String(selectedId)]);
        if (fromMap) return fromMap;
      } catch (e) {
        // ignore invalid map JSON
      }
    }

    return parseVariantId(form.dataset.vrgVariantId);
  }

  async function fetchCart() {
    const response = await fetch(`${getRoutes().cart}.js`, {
      headers: { Accept: 'application/json' }
    });
    if (!response.ok) throw new Error(String(response.status));
    return response.json();
  }

  /**
   * All VRG fee lines that reference a carrier variant. Legacy carts can hold
   * more than one fee line per carrier, so every match is returned.
   * @param {object} cart - Cart JSON.
   * @param {string|number} carrierVariantId - Carrier variant id.
   * @returns {Array}
   */
  function findVrgLines(cart, carrierVariantId) {
    const carrierId = parseVariantId(carrierVariantId);
    if (!carrierId) return [];

    return (cart.items || []).filter((item) => {
      const props = item.properties || {};
      return parseVariantId(props[VRG_FOR_KEY]) === carrierId;
    });
  }

  /**
   * First VRG fee line that references a carrier variant.
   * @param {object} cart - Cart JSON.
   * @param {string|number} carrierVariantId - Carrier variant id.
   * @returns {object|undefined}
   */
  function findVrgLine(cart, carrierVariantId) {
    return findVrgLines(cart, carrierVariantId)[0];
  }

  /**
   * Combined quantity of all carrier lines of a variant. A pending change to a
   * single line can be passed in, so the fee line can be set to the quantity
   * the cart will have after the mutation.
   * @param {object} cart - Cart JSON.
   * @param {string|number} carrierVariantId - Carrier variant id.
   * @param {object} [pending] - Pending change, `lineKey` and `quantity`.
   * @returns {number}
   */
  function sumCarrierQuantity(cart, carrierVariantId, pending = {}) {
    const carrierId = parseVariantId(carrierVariantId);
    if (!carrierId) return 0;

    const pendingKey = pending.lineKey || null;
    const pendingQuantity = Math.max(0, Number(pending.quantity) || 0);

    return (cart.items || []).reduce((total, item) => {
      const props = item.properties || {};
      if (props[VRG_FOR_KEY]) return total;
      if (parseVariantId(item.variant_id) !== carrierId) return total;
      if (pendingKey && item.key === pendingKey) return total + pendingQuantity;
      return total + Number(item.quantity || 0);
    }, 0);
  }

  /**
   * Resolves a cart line by its line item key, falling back to the 1-based line
   * index the row was rendered with. Line keys change whenever properties or
   * discounts of a line change, so the index is kept as a safety net.
   * @param {object} cart - Cart JSON.
   * @param {string} lineKey - Line item key.
   * @param {number|string} line - 1-based line index.
   * @returns {object|null}
   */
  function findLine(cart, lineKey, line) {
    const items = cart.items || [];
    const byKey = lineKey ? items.find((item) => item.key === lineKey) : null;
    if (byKey) return byKey;

    const index = Number(line);
    if (!Number.isFinite(index) || index <= 0) return null;
    return items[index - 1] || null;
  }

  /**
   * Converts a single-item product FormData into a multi-item payload that
   * also includes the VRG fee line when applicable.
   */
  function withVrgItems(formData, form) {
    const vrgVariantId = getVrgVariantIdFromForm(form);
    const carrierVariantId = parseVariantId(formData.get('id') || form?.querySelector?.('[name="id"]')?.value);
    if (!vrgVariantId || !carrierVariantId || vrgVariantId === carrierVariantId) {
      return formData;
    }

    const hasItemsIndex = [...formData.keys()].some((key) => /^items\[\d+]\[id]$/.test(key));
    if (hasItemsIndex) {
      let maxIndex = -1;
      for (const key of formData.keys()) {
        const match = key.match(/^items\[(\d+)]/);
        if (match) maxIndex = Math.max(maxIndex, Number(match[1]));
      }
      const index = maxIndex + 1;
      const qty = formData.get(`items[0][quantity]`) || formData.get('quantity') || '1';
      formData.append(`items[${index}][id]`, String(vrgVariantId));
      formData.append(`items[${index}][quantity]`, String(qty));
      formData.append(`items[${index}][properties][${VRG_FOR_KEY}]`, String(carrierVariantId));
      return formData;
    }

    const productId = formData.get('id');
    const productQty = formData.get('quantity') || '1';
    if (!productId) return formData;

    const next = new FormData();
    next.append('items[0][id]', String(productId));
    next.append('items[0][quantity]', String(productQty));

    for (const [key, value] of formData.entries()) {
      if (key === 'id' || key === 'quantity') continue;
      if (key === 'sections' || key === 'sections_url') {
        next.append(key, value);
        continue;
      }
      const propMatch = key.match(/^properties\[(.+)]$/);
      if (propMatch) {
        next.append(`items[0][properties][${propMatch[1]}]`, value);
        continue;
      }
      next.append(key, value);
    }

    next.append('items[1][id]', String(vrgVariantId));
    next.append('items[1][quantity]', String(productQty));
    next.append(`items[1][properties][${VRG_FOR_KEY}]`, String(productId));
    return next;
  }

  /**
   * Requests rendered section markup, shaped like the `sections` key of a
   * bundled cart response.
   * @param {Array|string} sections - Section ids.
   * @param {string} sectionsUrl - Page the sections are rendered in.
   * @returns {Promise<object>}
   */
  async function fetchSections(sections, sectionsUrl) {
    const url = sectionsUrl || window.location.pathname;
    const separator = url.includes('?') ? '&' : '?';
    const response = await fetch(`${url}${separator}sections=${[].concat(sections).join(',')}`);
    if (!response.ok) throw new Error(String(response.status));
    return response.json();
  }

  /**
   * Applies several line quantity changes within one single cart mutation.
   * Carrier and fee lines have to travel in the same request, otherwise the cart
   * is briefly persisted with a mismatched VRG pairing, which the cart
   * validation rejects with an error.
   * @param {object} updates - Map of line item key to quantity.
   * @param {object} [options] - `sections` and `sectionsUrl` for section rendering.
   * @returns {Promise<object>} Cart JSON, including `sections` when requested.
   */
  async function updateLines(updates, options = {}) {
    const body = { updates };
    if (options.sections) body.sections = options.sections;
    if (options.sectionsUrl) body.sections_url = options.sectionsUrl;

    const response = await fetch(getRoutes().cartUpdate, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(body)
    });
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.description || data.errors || data.message || response.status);
    }

    if (options.sections && !data.sections) {
      data.sections = await fetchSections(options.sections, options.sectionsUrl);
    }
    return data;
  }

  /**
   * Adds a VRG fee line for a carrier variant.
   * @param {number} vrgVariantId - Fee variant id.
   * @param {number} quantity - Fee quantity.
   * @param {number} carrierVariantId - Carrier variant id, stored in `_vrg_for`.
   * @param {object} [options] - `sections` and `sectionsUrl` for section rendering.
   * @returns {Promise<object>}
   */
  async function addVrgLine(vrgVariantId, quantity, carrierVariantId, options = {}) {
    const formData = new FormData();
    formData.append('items[0][id]', String(vrgVariantId));
    formData.append('items[0][quantity]', String(quantity));
    formData.append(`items[0][properties][${VRG_FOR_KEY}]`, String(carrierVariantId));
    if (options.sections) formData.append('sections', [].concat(options.sections).join(','));
    if (options.sectionsUrl) formData.append('sections_url', options.sectionsUrl);

    const response = await fetch(getRoutes().cartAdd, {
      method: 'POST',
      headers: {
        Accept: 'application/javascript',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: formData
    });
    const data = await response.json();
    if (!response.ok || data.status) {
      throw new Error(data.description || data.message || response.status);
    }
    return data;
  }

  /**
   * Changes the quantity of a carrier line together with its VRG fee line(s) in
   * a single cart mutation, so the `_vrg_for` pairing always stays intact.
   * @param {object} options - Change description: `line` (1-based index),
   *   `lineKey`, `carrierVariantId`, `quantity`, `vrgVariantId`, `sections` and
   *   `sectionsUrl`.
   * @returns {Promise<object>} Cart JSON, including `sections` when requested.
   */
  async function updateCarrierLine(options) {
    const {
      line, lineKey, carrierVariantId, quantity, vrgVariantId, sections, sectionsUrl
    } = options;
    const sectionOptions = { sections, sectionsUrl };

    const cart = await fetchCart();
    const carrierLine = findLine(cart, lineKey, line);
    if (!carrierLine) throw new Error(window.theme?.strings?.cartError || 'Cart error');

    const targetQuantity = Math.max(0, Number(quantity) || 0);
    const carrierId = parseVariantId(carrierVariantId) || parseVariantId(carrierLine.variant_id);
    if (!carrierId) return updateLines({ [carrierLine.key]: targetQuantity }, sectionOptions);

    const feeLines = findVrgLines(cart, carrierId);
    const feeQuantity = sumCarrierQuantity(cart, carrierId, {
      lineKey: carrierLine.key,
      quantity: targetQuantity
    });

    // Duplicated fee lines are consolidated on the first one and dropped, so a
    // quantity change can never leave an orphaned fee line behind.
    const updates = { [carrierLine.key]: targetQuantity };
    feeLines.forEach((feeLine, index) => {
      updates[feeLine.key] = index === 0 ? feeQuantity : 0;
    });

    const data = await updateLines(updates, sectionOptions);

    if (feeLines.length > 0 || feeQuantity <= 0) return data;

    // Carrier without a fee line, e.g. added to the cart before the pairing
    // existed. The cart is consistent at this point, so adding it is safe.
    const feeVariantId = parseVariantId(vrgVariantId);
    if (!feeVariantId || feeVariantId === carrierId) return data;

    const added = await addVrgLine(feeVariantId, feeQuantity, carrierId, sectionOptions);
    const updatedCart = await fetchCart();
    return { ...updatedCart, sections: added.sections || data.sections };
  }

  /**
   * Removes VRG fee lines whose carrier product is no longer in the cart. Such a
   * leftover makes the cart validation reject every further cart change, so it
   * has to go before the customer can edit the cart again.
   * @returns {Promise<boolean>} Whether the cart was changed.
   */
  async function removeOrphanedVrgLines() {
    const cart = await fetchCart();
    const updates = {};

    (cart.items || []).forEach((item) => {
      const carrierVariantId = parseVariantId((item.properties || {})[VRG_FOR_KEY]);
      if (!carrierVariantId) return;
      if (sumCarrierQuantity(cart, carrierVariantId) > 0) return;
      updates[item.key] = 0;
    });

    if (Object.keys(updates).length === 0) return false;

    await updateLines(updates);
    return true;
  }

  /**
   * Full cart reconcile based on DOM carrier hints + existing `_vrg_for` pairs.
   * @returns {Promise<boolean>}
   */
  async function reconcile() {
    if (syncing) return false;
    syncing = true;
    let changed = false;

    try {
      let cart = await fetchCart();
      const updates = {};
      const handledCarriers = new Set();

      // Mirror the carrier quantities onto their fee lines and drop orphaned as
      // well as duplicated fee lines - all within one mutation, so the cart
      // never passes through a state the cart validation would reject.
      for (const item of cart.items || []) {
        const props = item.properties || {};
        const carrierVariantId = parseVariantId(props[VRG_FOR_KEY]);
        if (!carrierVariantId) continue;

        const isDuplicate = handledCarriers.has(carrierVariantId);
        handledCarriers.add(carrierVariantId);

        const carrierQty = isDuplicate ? 0 : sumCarrierQuantity(cart, carrierVariantId);
        if (Number(item.quantity) !== carrierQty) updates[item.key] = carrierQty;
      }

      if (Object.keys(updates).length > 0) {
        await updateLines(updates);
        changed = true;
        cart = await fetchCart();
      }

      const carrierNeeds = new Map();
      document.querySelectorAll('.cart-item[data-vrg-variant-id]').forEach((row) => {
        if (row.dataset.vrgFor) return;
        const carrierVariantId = parseVariantId(row.dataset.variantId);
        const vrgVariantId = parseVariantId(row.dataset.vrgVariantId);
        if (!carrierVariantId || !vrgVariantId) return;
        carrierNeeds.set(carrierVariantId, vrgVariantId);
      });

      for (const [carrierVariantId, vrgVariantId] of carrierNeeds.entries()) {
        const carrierQty = sumCarrierQuantity(cart, carrierVariantId);
        if (carrierQty <= 0) continue;
        if (findVrgLine(cart, carrierVariantId)) continue;
        await addVrgLine(vrgVariantId, carrierQty, carrierVariantId);
        changed = true;
      }
    } finally {
      syncing = false;
    }

    return changed;
  }

  return {
    VRG_FOR_KEY,
    parseVariantId,
    getVrgVariantIdFromForm,
    withVrgItems,
    updateLines,
    updateCarrierLine,
    removeOrphanedVrgLines,
    reconcile,
    findVrgLine,
    findVrgLines,
    fetchCart
  };
})();
